import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Container, PageHeader, SectionHead, Picture, QuoteForm, ContactButtons, FaqList, INKS } from "@/components/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const steps = [
  { title: "Brief", body: "Share the occasion, quantity, budget and brand guidelines." },
  { title: "Mock-up", body: "We send visuals with your logo, colours and any personalisation." },
  { title: "Sample", body: "Approve a pre-production sample before the bulk run." },
  { title: "Deliver", body: "To one office, several, or individual addresses across India." },
];

const occasions = [
  { title: "Diwali and festive", body: "Branded hampers, sweet boxes and premium gift sets." },
  { title: "New Year", body: "Custom diaries, calendars, planners and desk accessories." },
  { title: "Onboarding kits", body: "T-shirts, mugs and notebooks to welcome new hires." },
  { title: "Client appreciation", body: "Considered gifts for the people who keep you in business." },
  { title: "Conferences and events", body: "Delegate kits, speaker gifts and branded giveaways." },
  { title: "Work anniversaries", body: "Personalised awards, trophies and hampers for milestones." },
];

const promises = [
  "Your branding on every piece, with individual names if needed",
  "Bulk pricing with nothing hidden",
  "A sample to approve before production",
  "Eco-friendly options: recycled paper, bamboo, jute, seed paper",
  "Pan-India delivery, to one address or many",
  "One account manager from brief to delivery",
];

const testimonials = [
  { name: "Meera Iyer", role: "HR Director, Nexus Technologies", quote: "Diwali gifting for 300+ employees, handled flawlessly, with individual names printed on every box." },
  { name: "Divya Ramesh", role: "Events Manager, Pinnacle Events", quote: "500 delegate kits with bags, notepads, pens and badges. Everything was perfect and arrived a day early." },
  { name: "Priyanka Das", role: "Marketing Lead, CloudSync", quote: "Three years of corporate calendars and diaries. Consistent quality, and they always make our November deadline." },
];

const faqs = [
  { q: "What's the minimum order?", a: "25 pieces for most items and 10 sets for custom hampers. Need fewer? Ask — we're flexible." },
  { q: "How early should we order?", a: "7–10 working days for standard items, 15–20 for custom hampers. Rush orders in 3–5 days cost extra." },
  { q: "Can you add individual names?", a: "Yes. We print individual names on diaries, mugs, boxes and more." },
  { q: "Can we see a sample first?", a: "Yes. We make a pre-production sample for approval, and its cost is adjusted against the final order." },
  { q: "Do you deliver to several locations?", a: "Yes — to multiple offices or straight to employees' homes anywhere in India." },
  { q: "What kinds of branding can you do?", a: "Printing, embossing, foil stamping, screen printing, sublimation and engraving, depending on the product." },
  { q: "Do you handle packaging?", a: "Yes: premium gift boxes, ribbon wrapping, and custom printed or branded packaging." },
];

export default function CorporateGiftingPublicPage() {
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/gifting-categories`).then((r) => setCategories(r.data || [])).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  return (
    <div data-testid="corporate-gifting-page" className="min-h-screen bg-paper">
      <Navbar />
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Corporate gifting" }]}
        title="Branded gifts people actually keep"
        lede="Onboarding kits, festive hampers and conference bags, branded and packed in Bengaluru and delivered anywhere in India. From 25 pieces."
      >
        <div className="flex flex-wrap gap-3">
          <a href="#gifting-form" className="btn btn-magenta">Get a gifting quote</a>
          <ContactButtons message="Hi, I'd like a quote for corporate gifts." />
        </div>
      </PageHeader>

      <main>
        <section className="py-16 md:py-24" aria-labelledby="catalogue">
          <Container>
            <SectionHead id="catalogue" title="The catalogue" lede="Pick a category to see items, colours and starting prices." />
            {categories.length > 0 ? (
              <ul className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
                {categories.map((cat, i) => (
                  <li key={cat.id}>
                    <Link to={`/corporate-gifting/${cat.id}`} data-testid={`pub-cat-${i}`}
                      className="group flex h-full flex-col bg-stock border border-ink transition-[transform,box-shadow] duration-150 hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#E5007D]">
                      <Picture src={cat.image} alt={cat.name} className="w-full aspect-square border-b border-ink" />
                      <span className="p-5 flex flex-1 items-start justify-between gap-3">
                        <span>
                          <span className="block font-display font-bold text-xl text-ink">{cat.name}</span>
                          {cat.description && <span className="mt-1 block text-sm text-graphite line-clamp-2">{cat.description}</span>}
                        </span>
                        <ArrowUpRight size={20} className="shrink-0 text-ink group-hover:text-process-magenta transition-colors" aria-hidden="true" />
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : loaded ? (
              <div className="border border-dashed border-ink p-10 text-center">
                <p className="font-display font-semibold text-xl text-ink">The online catalogue is being updated.</p>
                <p className="mt-2 text-graphite">Tell us what you're looking for and we'll send options and prices.</p>
                <a href="#gifting-form" className="btn btn-ink mt-6">Ask for options</a>
              </div>
            ) : null}
          </Container>
        </section>

        <section className="bg-process-yellow border-y border-ink py-16 md:py-24" aria-labelledby="process">
          <Container>
            <SectionHead id="process" title="How gifting orders work" />
            <ol className="grid gap-px bg-ink border border-ink sm:grid-cols-2 lg:grid-cols-4">
              {steps.map((s, i) => (
                <li key={s.title} className="bg-process-yellow p-6 sm:p-8" data-testid={`step-${i}`}>
                  <span className="font-display font-bold text-6xl leading-none text-ink" aria-hidden="true">{i + 1}</span>
                  <h3 className="mt-6 font-display font-bold text-xl text-ink">{s.title}</h3>
                  <p className="mt-2 text-ink/80 leading-relaxed">{s.body}</p>
                </li>
              ))}
            </ol>
          </Container>
        </section>

        <section className="py-16 md:py-24" aria-labelledby="occasions">
          <Container className="grid gap-12 lg:grid-cols-[1fr_1.6fr]">
            <div>
              <h2 id="occasions" className="font-display font-bold text-3xl sm:text-5xl text-ink">Occasions we gift for</h2>
              <h3 className="mt-10 font-display font-semibold text-graphite">Every order includes</h3>
              <ul className="mt-4 space-y-3">
                {promises.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-ink">
                    <Check size={18} className="mt-1 shrink-0 text-process-magenta" aria-hidden="true" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <ul className="grid sm:grid-cols-2 border-t border-l border-ink self-start">
              {occasions.map((o, i) => (
                <li key={o.title} className="border-r border-b border-ink p-6" data-testid={`occasion-${i}`}>
                  <span className="block w-6 h-1.5 mb-4" style={{ backgroundColor: INKS[i % 4] }} aria-hidden="true" />
                  <h3 className="font-display font-bold text-xl text-ink">{o.title}</h3>
                  <p className="mt-2 text-graphite leading-relaxed">{o.body}</p>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="bg-stock border-y border-ink py-16 md:py-24" aria-labelledby="stories">
          <Container>
            <SectionHead id="stories" title="From our gifting clients" />
            <ul className="grid gap-10 md:grid-cols-3">
              {testimonials.map((t, i) => (
                <li key={t.name} className="border-t-[3px] pt-6" style={{ borderColor: INKS[i] }}>
                  <blockquote>
                    <p className="font-display text-xl leading-snug text-ink">“{t.quote}”</p>
                    <footer className="mt-5 text-sm">
                      <span className="block font-semibold text-ink">{t.name}</span>
                      <span className="text-graphite">{t.role}</span>
                    </footer>
                  </blockquote>
                </li>
              ))}
            </ul>
          </Container>
        </section>

        <section className="py-16 md:py-24" aria-labelledby="gift-faq">
          <Container className="grid gap-10 lg:grid-cols-[1fr_1.7fr]">
            <h2 id="gift-faq" className="font-display font-bold text-3xl sm:text-5xl text-ink">Gifting questions</h2>
            <FaqList faqs={faqs} testid="gifting-faq" />
          </Container>
        </section>

        <section id="gifting-form" className="bg-process-magenta border-t border-ink py-16 md:py-24 scroll-mt-16">
          <Container className="grid gap-10 lg:grid-cols-[1fr_minmax(0,32rem)] lg:gap-16 items-start">
            <div className="text-white">
              <h2 className="font-display font-bold text-4xl sm:text-6xl leading-[0.98] tracking-[-0.035em]">Plan your gifting order</h2>
              <p className="mt-5 text-lg text-white/90 leading-relaxed max-w-md">
                Tell us the occasion, how many people, and your budget per gift. We'll come back with options within one working day.
              </p>
            </div>
            <QuoteForm
              testid="gifting-quote-form"
              title="Gifting enquiry"
              service="Corporate Gifting"
              lockService
              messageLabel="Occasion, quantity and budget"
              messagePlaceholder="e.g. Diwali, 120 employees, around ₹800 a gift"
              submitLabel="Send gifting enquiry"
              className="shadow-[8px_8px_0_0_#161616]"
            />
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
}
